import {
  AuthenticationResult,
  AuthError,
  EventMessage,
  EventType,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { PropsWithChildren, useEffect } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'routing';
import { PERMISSIONS } from 'shared/constants';
import { useAuthState } from 'shared/stores';
import { InsightB2CClaims } from 'shared/types';
import { getAppPermissions, getUserInfoFromIdClaims } from 'shared/utility';
import { CustomNavigationClient } from 'startup';

type AuthProviderProps = {
  clientApp: IPublicClientApplication;
};

export function ProvideAuth({ children, clientApp }: PropsWithChildren<AuthProviderProps>) {
  const history = useHistory();
  const authState = useAuthState();
  const handleError = useErrorHandler();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const navigationClient = new CustomNavigationClient(history);
    clientApp.setNavigationClient(navigationClient);
    const accounts = clientApp.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      clientApp.setActiveAccount(account);
      const user = getUserInfoFromIdClaims(account.idTokenClaims as InsightB2CClaims);
      authState.user.set(user);
    }

    const callbackId = clientApp.addEventCallback((event: EventMessage) => {
      switch (event.eventType) {
        case EventType.LOGIN_SUCCESS:
          const payload = event.payload as AuthenticationResult;
          // Verify Insight claims here.
          const account = payload.account;
          clientApp.setActiveAccount(account);
          const user = getUserInfoFromIdClaims(account?.idTokenClaims as InsightB2CClaims);
          const appPermissions = getAppPermissions(user.insightClaims);
          if (!appPermissions.includes(PERMISSIONS.login)) {
            enqueueSnackbar(
              'Login Successful, but you do not have permission to access this application.\nlogging out now...',
              { variant: 'error' }
            );
            history.push(ROUTES.unauthorized);
            setTimeout(() => {
              clientApp.logout();
            }, 3500);
          }
          authState.user.set(user);
          break;
        case EventType.ACQUIRE_TOKEN_SUCCESS:
          break;
        case EventType.LOGOUT_END:
          authState.user.set(null);
          break;
        case EventType.LOGIN_FAILURE:
        case EventType.ACQUIRE_TOKEN_FAILURE:
        case EventType.LOGOUT_FAILURE:
          const authError = event.error as AuthError;
          handleError(authError);
          break;
        default:
          break;
      }
    });

    return () => {
      if (callbackId) {
        clientApp.removeEventCallback(callbackId);
      }
    };
  }, [clientApp, history, authState, handleError, enqueueSnackbar]);

  return <MsalProvider instance={clientApp}>{children}</MsalProvider>;
}

export const AuthInProgress = () => <Typography variant='h4'>Authorization in progress...</Typography>;
